import Button from '@risksmart-app/components/src/button';
import {
  useGetGuidParam,
  useGetOptionalGuidParam,
} from '@risksmart-app/components/src/routes/routes.utils';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteOversightResults } from '@/hooks/mutations/oversight-result/useDeleteOversightResults';
import { useGetOversightAssessmentById, useGetOversightResultById } from '@/hooks/queries';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { oversightAssessmentResultsUrl } from '@/utils/urls';

import { useTabs } from '../useTabs';

const Page: FC = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'assessmentResults' });
  const title = t('edit_title');
  const optionalAssessmentId = useGetOptionalGuidParam('assessmentId');
  const findingId = useGetGuidParam('findingId');
  const navigate = useNavigate();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const { data: resultData, error: resultError } = useGetOversightResultById({
    queryArgs: { id: findingId },
    shouldSkip: !!optionalAssessmentId,
  });
  if (resultError) {
    throw resultError;
  }
  const resultParentId = resultData?.oversight_result_parent.find(
    // RSP-6515 (Wave C1): match both discriminators for the deploy window — a
    // not-yet-upgraded app may have written the legacy value on this parent row.
    // Contracts to oversight-only in C2 (RSP-6516).
    (ar) =>
      ar.ParentType === ParentTypes.OversightAssessment ||
      ar.ParentType === ParentTypes.ComplianceMonitoringAssessment
  )?.ParentId;
  const assessmentId = optionalAssessmentId ?? resultParentId;

  const { data, error } = useGetOversightAssessmentById({
    queryArgs: { id: assessmentId! },
    shouldSkip: !assessmentId,
  });
  if (error) {
    throw error;
  }

  const assessment = data?.oversight_assessment?.[0];

  const {
    hasPermission: canDeleteDocumentOversightResult,
    loading: canDeleteDocumentLoading,
  } = useHasPermissionQuery('delete:document_second_line_result', assessment);
  const {
    hasPermission: canDeleteObligationOversightResult,
    loading: canDeleteObligationLoading,
  } = useHasPermissionQuery('delete:obligation_second_line_result', assessment);
  const {
    hasPermission: canDeleteRiskControlledOversightResult,
    loading: canDeleteRiskControlledLoading,
  } = useHasPermissionQuery(
    'delete:risk_controlled_second_line_result',
    assessment
  );
  const {
    hasPermission: canDeleteRiskUncontrolledOversightResult,
    loading: canDeleteRiskUncontrolledLoading,
  } = useHasPermissionQuery(
    'delete:risk_uncontrolled_second_line_result',
    assessment
  );
  const {
    hasPermission: canDeleteControlTestOversightResult,
    loading: canDeleteControlTestLoading,
  } = useHasPermissionQuery(
    'delete:control_test_second_line_result',
    assessment
  );

  const isPermissionLoading =
    canDeleteDocumentLoading ||
    canDeleteObligationLoading ||
    canDeleteRiskControlledLoading ||
    canDeleteRiskUncontrolledLoading ||
    canDeleteControlTestLoading;

  const canDelete =
    !isPermissionLoading &&
    (canDeleteDocumentOversightResult ||
      canDeleteObligationOversightResult ||
      canDeleteRiskControlledOversightResult ||
      canDeleteRiskUncontrolledOversightResult ||
      canDeleteControlTestOversightResult);

  const { deleteOversightResults, loading: deleteLoading } =
    useDeleteOversightResults();

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    asyncAction: async () => {
      await deleteOversightResults([findingId]);
      setIsDeleteModalVisible(false);
      navigate(oversightAssessmentResultsUrl(assessmentId));

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const tabs = useTabs(
    assessment || undefined,
    'update',
    !optionalAssessmentId,
    !!optionalAssessmentId
  );

  return (
    <PageLayout
      title={title}
      actions={
        canDelete ? (
          <Button
            formAction={'none'}
            variant={'normal'}
            onClick={() => setIsDeleteModalVisible(true)}
          >
            {t('delete_button')}
          </Button>
        ) : undefined
      }
    >
      <ControlledTabs tabs={tabs} variant={'container'} parent={assessment} />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        loading={deleteLoading}
        header={t('delete_button')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {t('confirm_single_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
